import {register} from '@shopify/theme-sections';
import Flickity from 'flickity';

import {initFlickityResizeClass} from '../utils/flickity';

const selectors = {
  galleryContainer: '[data-gallery-container]',
  gallery: '[data-gallery]',
  galleryItem: '[data-gallery-item]',
  galleryOpen: '[data-gallery-open]',
  galleryClose: '[data-gallery-close]',
  cursor: '[data-gallery-cursor]',
  cursorText: '[data-gallery-cursor-text]',
};

const classes = {
  slider: 'article-gallery__slider',
  cursor: 'article-gallery__cursor',
  galleryIsActive: 'gallery-is-active',
};

const sectionType = 'article-gallery';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

export default isUnregistered &&
  register(sectionType, {
    onLoad() {
      const container = this.container;
      this.galleryContainer = container.querySelector(selectors.galleryContainer);
      this.galleryItems = container.querySelectorAll(selectors.galleryItem);

      this.modalIsActive = false;

      this.initGalleryModal();

      // Handle gallery open button click
      const galleryOpenBtn = container.querySelector(selectors.galleryOpen);
      galleryOpenBtn?.addEventListener('click', () => {
        this.openGallery(1);
      });

      // Handle gallery item click
      this.galleryItems?.forEach((galleryItem) => {
        galleryItem.addEventListener('click', (event) => {
          if (this.modalIsActive) return;

          const itemIndex = Number(event.currentTarget.dataset.galleryItem) ?? 1;
          this.openGallery(itemIndex);
        });
      });
    },

    initGalleryModal() {
      this.modal = document.createElement('div');
      this.modal.classList.add('gallery-modal');
      this.modal.innerHTML = this.galleryContainer.parentNode.innerHTML ?? '';
      this.modal.ariaHidden = true;

      this.slider = this.modal.querySelector(selectors.gallery);
      this.slider.classList.add(classes.slider);

      document.body.appendChild(this.modal);

      // Handle gallery close button click
      this.modalClose = this.modal.querySelector(selectors.galleryClose);
      this.modalClose?.addEventListener('click', this.closeGallery.bind(this));

      // Init cursor element and mousemove
      this.cursorElem = this.modal.querySelector(selectors.cursor);
      this.cursorInnerElem = this.cursorElem?.querySelector(selectors.cursorText);
      this.boundMoveCursorElem = this.moveCursorElem.bind(this);
    },

    openGallery(itemIndex) {
      this.modal.ariaHidden = false;
      document.body.classList.add(classes.galleryIsActive);

      // Initialise flickity and update image sizes attribute when gallery is opened the first time
      if (!this.flickity) {
        this.updateImageSizesAttr();

        initFlickityResizeClass();

        this.flickity = new Flickity(this.slider, {
          freeScroll: false,
          draggable: true,
          pageDots: false,
          setGallerySize: true,
          contain: true,
          cellAlign: 'left',
        });
      }

      this.flickity?.select(itemIndex - 1, false, true);

      this.modalIsActive = true;

      this.updateCursorElemText(itemIndex);

      this.slider?.addEventListener('mousemove', this.boundMoveCursorElem);
    },

    closeGallery() {
      this.modal.ariaHidden = true;
      document.body.classList.remove(classes.galleryIsActive);
      this.modalIsActive = false;

      this.slider?.removeEventListener('mousemove', this.boundMoveCursorElem);
    },

    updateCursorElemText(currentIndex) {
      const totalSlides = this.flickity.slides.length;

      this.cursorInnerElem.textContent = `${currentIndex} of ${totalSlides}`;

      this.flickity.on('select', (index) => {
        this.cursorInnerElem.textContent = `${index + 1} of ${totalSlides}`;
      });
    },

    moveCursorElem(event) {
      if (!this.cursorElem) return;
      this.cursorElem.style.transform = `translate(${event.clientX}px, ${event.clientY}px)`;

      const isPrev = event.clientX < window.innerWidth / 2;
      const cursorPosition = isPrev ? 'left' : 'right';
      const classesToRemove = Array.from(this.cursorElem.classList).filter((value) =>
        value.includes(`${classes.cursor}--`)
      );

      this.cursorElem.classList.remove(...classesToRemove);
      this.cursorElem.classList.add(`${classes.cursor}--${cursorPosition}`);
    },

    updateImageSizesAttr() {
      const galleryImages = this.slider.querySelectorAll('img');
      galleryImages?.forEach((image) => {
        const imageParent = image.closest(selectors.galleryItem);
        if (!imageParent) return;
        image.sizes = imageParent.dataset?.imageSizes;
      });
    },
  });
